import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTenant } from "../../../Contexts/TenantContext";
import { useAuth } from "../../../Contexts/AuthContext";
import { useCart } from "../../../Contexts/CartContext";
import { orderService } from "../../../Api/Services/orderService";
import { couponService } from "../../../Api/Services/couponService";
import { paymentService } from "../../../Api/Services/paymentService";
import { createOrderHubConnection } from "../../../Api/orderHub";
import { CreateOrderResponse } from "../../../Models/Order";
import {
  BankingBilletChargeResponse,
} from "../../../Models/Payment";
import { InStoreUserRegister } from "../../../Models/User";
import { PaymentMethod } from "../../../Enums/PaymentMethod";
import { CustomerType } from "../../../Enums/CustomerType";
import { formatCnpj, formatCpf, formatPriceBRL } from "../../../Utils/DataFormatters";
import { parseCurrencyInput } from "../../../Utils/InputParsers";
import { Input } from "../../../Components/Input/Input";
import { needsPaymentSync, PaymentSyncSnapshot, syncPaymentMethod } from "../../../Utils/paymentSync";
import {
  FaCreditCard,
  FaMoneyBillAlt,
  FaRegEye,
  FaShoppingCart,
  FaUser,
} from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import ProgressBar from "../../../Components/ProgressBar/ProgressBar";
import CustomerStep from "../../../Components/CustomerStep/CustomerStep";
import ProductStep from "../../../Components/ProductStep/ProductStep";
import ReviewStep from "../../../Components/ReviewStep/ReviewStep";
import OrderSummary from "../../../Components/OrderSummary/OrderSummary";
import CouponInput from "../../../Components/CouponInput/CouponInput";
import OrderNavigation from "../../../Components/OrderNavigation/OrderNavigation";
import InStorePaymentStep from "../../../Components/InStorePaymentStep/InStorePaymentStep";
import LoadingOverlay from "../../../Components/LoadingOverlay/LoadingOverlay";
import BankingBilletStep from "../../../Components/BankingBilletStep/BankingBilletStep";
import InStorePixStep from "../../../Components/InStorePixStep/InStorePixStep";

const AdminInStoreOrderNewPage = () => {
  const navigate = useNavigate();
  const { tenantPreference } = useTenant();
  const { isAdmin, registerInStore } = useAuth();
  const { cartItems, getTotal, clearCart } = useCart();

  const basePath = isAdmin ? "/admin" : "/operador";

  const [loading, setLoading] = useState<boolean>(false);
  const [order, setOrder] = useState<CreateOrderResponse | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const customerStepRef = useRef<any>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [newCustomerForm, setNewCustomerForm] = useState<InStoreUserRegister | null>(null);
  const [selectedCustomerInfo, setSelectedCustomerInfo] = useState<{
    name: string;
    document: string;
    customerType: CustomerType | null;
  } | null>(null);

  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [couponDiscountPercentage, setCouponDiscountPercentage] = useState<number | null>(null);

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [bankingBilletResult, setBankingBilletResult] = useState<BankingBilletChargeResponse | null>(null);
  const [orderPaymentSnapshot, setOrderPaymentSnapshot] = useState<PaymentSyncSnapshot | null>(null);

  const hubRef = useRef<ReturnType<typeof createOrderHubConnection> | null>(null);
  const orderIdRef = useRef<string | null>(null);
  const paymentMethodRef = useRef<PaymentMethod | null>(null);
  const navigateRef = useRef(navigate);
  const basePathRef = useRef(basePath);
  navigateRef.current = navigate;
  basePathRef.current = basePath;

  const [steps, setSteps] = useState([
    { label: "Cliente", icon: <FaUser /> },
    { label: "Produtos", icon: <FaShoppingCart /> },
  ]);

  const [isNoProductsMode, setIsNoProductsMode] = useState(false);
  const [manualOrderValue, setManualOrderValue] = useState<number | null>(null);

  const bankingBilletFeeValue = tenantPreference?.bankingBilletFee ?? 0;

  const total = getTotal();
  const couponDiscountAmount = couponDiscountPercentage ? (total * couponDiscountPercentage) / 100 : 0;

  const getCreditCardInterest = () => 0;

  const creditCardInterest = getCreditCardInterest();
  const isOrderCompleteStep = currentStep === steps.length - 1 && order !== null;
  const isPixCompleteStep = isOrderCompleteStep && selectedPaymentMethod === PaymentMethod.Pix;
  const showSummary = true;

  useEffect(() => { orderIdRef.current = order?.id ?? null; }, [order?.id]);
  useEffect(() => { paymentMethodRef.current = selectedPaymentMethod; }, [selectedPaymentMethod]);

  useEffect(() => {
    if (hubRef.current) {
      return;
    }
    const hub = createOrderHubConnection();
    hubRef.current = hub;
    const handleStatusChanged = (payload: { orderId?: string; status?: string }) => {
      if (!payload?.orderId || payload.orderId !== orderIdRef.current) {
        return;
      }
      if (payload.status === "Paid" || payload.status === "Shipped") {
        const method = paymentMethodRef.current;
        if (method === PaymentMethod.BankingBillet) {
          toast.success("Pagamento confirmado! Pedido criado com sucesso.");
          navigateRef.current(basePathRef.current + "/balcao");
        }
      }
    };
    hub.on("OrderStatusChanged", handleStatusChanged);
    hub.start().catch(() => {});
    return () => {
      hub.off("OrderStatusChanged", handleStatusChanged);
      hub.stop();
      hubRef.current = null;
    };
  }, []);

  const updateStepsForPaymentMethod = (method: PaymentMethod | null, noProducts?: boolean): void => {
    const withNoProducts = noProducts ?? isNoProductsMode;
    const base = [
      { label: "Cliente", icon: <FaUser /> },
      { label: "Produtos", icon: <FaShoppingCart /> },
      withNoProducts ? { label: "Valor", icon: <FaMoneyBillAlt /> } : { label: "Confirmação", icon: <FaRegEye /> },
      { label: "Pagamento", icon: <FaCreditCard /> },
    ];
    if (method == null) { setSteps(base); return; }
    if (method === PaymentMethod.Pix) { setSteps([...base, { label: "PIX", icon: <FaRegEye /> }]); return; }
    if (method === PaymentMethod.BankingBillet) { setSteps([...base, { label: "Boleto", icon: <FaRegEye /> }]); return; }
    setSteps(base);
  };

  const nextStep = async () => {
    if (isOrderCompleteStep) {
      clearCart();
      toast.success("Pedido criado com sucesso!");
      navigate(basePath + "/balcao");
      return;
    }
    if (currentStep === 0) {
      setLoading(true);
      const valid = customerStepRef.current?.validate?.();
      if (!valid) { setLoading(false); return; }
      try {
        if (!selectedCustomerId && newCustomerForm) {
          const registerResult = await registerInStore(newCustomerForm);
          if (!registerResult || !registerResult.customerId) { setLoading(false); return; }
          setSelectedCustomerId(registerResult.customerId);
          const doc = registerResult.customerDocument ?? "";
          const type = registerResult.customerType ?? newCustomerForm.customerType;
          setSelectedCustomerInfo({
            name: registerResult.customerName ?? "",
            document: type === CustomerType.LegalEntity ? formatCnpj(doc) : formatCpf(doc),
            customerType: type,
          });
        }
      } catch { toast.error("Erro ao registrar cliente."); } finally { setLoading(false); }
    }
    if (currentStep === 1) {
      const noProducts = cartItems.length === 0;
      setIsNoProductsMode(noProducts);
      updateStepsForPaymentMethod(null, noProducts);
      setCurrentStep(2);
      return;
    }
    if (currentStep === 2 && isNoProductsMode) {
      if (!manualOrderValue || manualOrderValue <= 0) { toast.error("Informe um valor válido para o pedido."); return; }
    }
    if (currentStep === 3) {
      setLoading(true);
      if (selectedPaymentMethod === null) { toast.info("Selecione um método de pagamento para continuar."); setLoading(false); return; }
      try {
        const currentOrder = order ?? (await handleCreateOrder());
        if (!currentOrder) {
          return;
        }
        await handleCompleteOrder(currentOrder);
        return;
      } finally { setLoading(false); }
    }
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const prevStep = () => {
    if (currentStep === 0) {
      navigate(basePath + "/balcao");
    } else {
      if (currentStep === 3 && order !== null) {
        setOrder(null);
        setOrderPaymentSnapshot(null);
      }
      setCurrentStep((s) => Math.max(s - 1, 0));
    }
  };

  const handleCreateOrder = async (): Promise<CreateOrderResponse | null> => {
    try {
      const orderResponse = await orderService.createInStore({
        addressId: null,
        shippingMethod: null,
        shippingPrice: null,
        deliveryTime: null,
        paymentMethod: selectedPaymentMethod,
        products: cartItems.map((item) => ({ productId: item.id, quantity: item.quantity })),
        couponCode: appliedCoupon ?? null,
        couponDiscountPercentage: couponDiscountPercentage ?? null,
        creditCardInstallments: null,
        customerId: selectedCustomerId,
        manualFinalPrice: isNoProductsMode ? manualOrderValue : null,
      });

      setOrder(orderResponse);
      setOrderPaymentSnapshot({
        paymentMethod: orderResponse?.paymentMethod ?? null,
        creditCardInstallments: orderResponse?.creditCardInstallments ?? null,
      });
      return orderResponse;
    } catch {
      toast.error("Não foi possível criar o pedido, tente novamente mais tarde.");
      return null;
    }
  };

  const handleCompleteOrder = async (currentOrder: CreateOrderResponse | null = order): Promise<void> => {
    if (selectedPaymentMethod === null || !currentOrder) {
      return;
    }
    const method = selectedPaymentMethod as PaymentMethod;
    try {
      const targetSnapshot: PaymentSyncSnapshot = {
        paymentMethod: method,
        creditCardInstallments: null,
      };
      const currentSnapshot: PaymentSyncSnapshot = orderPaymentSnapshot ?? {
        paymentMethod: currentOrder.paymentMethod ?? null,
        creditCardInstallments: currentOrder.creditCardInstallments ?? null,
      };
      const shouldSync = needsPaymentSync(currentSnapshot, targetSnapshot);
      if (shouldSync) {
        const synced = await syncPaymentMethod({
          orderId: currentOrder.id,
          paymentMethod: method,
          creditCardInstallments: targetSnapshot.creditCardInstallments ?? null,
        });
        if (!synced) { toast.error("Não foi possível atualizar o método de pagamento. Tente novamente."); return; }
        setOrderPaymentSnapshot(targetSnapshot);
      }
      switch (method) {
        case PaymentMethod.CreditCard:
        case PaymentMethod.Cash: {
          toast.success("Pedido criado com sucesso!");
          navigate(basePath + "/balcao");
          return;
        }
        case PaymentMethod.Pix: {
          setCurrentStep(steps.length - 1);
          return;
        }
        case PaymentMethod.BankingBillet: {
          const billetResult = await paymentService.createBankingBilletCharge({ orderId: currentOrder.id });
          setBankingBilletResult(billetResult);
          setCurrentStep(steps.length - 1);
          return;
        }
        default: return;
      }
    } catch { toast.error("Não foi possível completar o pedido, tente novamente mais tarde."); }
    finally { setLoading(false); }
  };

  const handleApplyCoupon = async (code: string) => {
    if (!code) { toast.error("Informe um código de cupom."); return; }
    try {
      const coupon = await couponService.validate(code);
      setCouponDiscountPercentage(coupon.discountPercentage);
      setAppliedCoupon(coupon.code);
      toast.success(`Cupom "${coupon.code}" aplicado!`);
    } catch (error: any) {
      if (error.response?.status === 404) { toast.error("Não foi encontrado cupom ativo com esse código."); }
      else { toast.error("Não foi possível aplicar o cupom de desconto, tente novamente mais tarde."); }
    }
  };

  const handleRemoveCoupon = () => { setCouponDiscountPercentage(null); setAppliedCoupon(null); toast.success(`Cupom removido!`); };

  const handlePaymentMethodChange = async (method: PaymentMethod) => {
    setSelectedPaymentMethod(method);
    updateStepsForPaymentMethod(method);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col overflow-x-hidden relative bg-gray-50">
        <SmartNavbar />
        <LoadingOverlay />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden bg-gray-50">
      <SmartNavbar />
      <main className="w-full flex flex-col items-center flex-1 min-h-0 py-10">
        <div className="w-full flex flex-col gap-9">
          <ProgressBar steps={steps} currentStep={currentStep} />
          <div className="flex flex-col lg:flex-row gap-7 px-4 sm:px-14">
            {currentStep === 0 && (
              <CustomerStep
                ref={customerStepRef}
                selectedCustomerId={selectedCustomerId}
                onSelectCustomer={(id, newCustomerData, existingInfo) => {
                  setSelectedCustomerId(id);
                  setNewCustomerForm(newCustomerData ?? null);
                  if (newCustomerData) { setSelectedCustomerInfo(null); }
                  else if (existingInfo) { setSelectedCustomerInfo({ name: existingInfo.name, document: existingInfo.document, customerType: existingInfo.customerType ?? null }); }
                  else { setSelectedCustomerInfo(null); }
                }}
              />
            )}
            {currentStep === 1 && <ProductStep />}
            {currentStep === 2 && !isNoProductsMode && (
              <ReviewStep cartItems={cartItems} selectedAddress={null} selectedShipping={null} />
            )}
            {currentStep === 2 && isNoProductsMode && (
              <div className="flex flex-col bg-white border p-6 gap-6 flex-[1.6] rounded shadow">
                <div className="flex items-center gap-2">
                  <FaMoneyBillAlt className="text-primary-600" />
                  <h4 className="text-md font-semibold">{"VALOR DO PEDIDO"}</h4>
                </div>
                <p className="text-sm text-gray-600">{"Informe o valor total da compra."}</p>
                <div className="w-full sm:w-1/2">
                  <Input
                    label={"Valor"}
                    type="text"
                    name="manualFinalPrice"
                    value={formatPriceBRL(manualOrderValue ?? 0) || ""}
                    onChange={(e) => setManualOrderValue(parseCurrencyInput(e.target.value) || null)}
                    required
                  />
                </div>
              </div>
            )}
            {currentStep === 3 && (
              <InStorePaymentStep
                selectedPaymentMethod={selectedPaymentMethod}
                onSelectPaymentMethod={handlePaymentMethodChange}
                enablePix={tenantPreference.enablePix}
                enableBankingBillet={tenantPreference.enableBankingBillet}
                enableCreditCard={tenantPreference.enableCreditCard}
              />
            )}
            {order && currentStep === steps.length - 1 && (
              <>
                {selectedPaymentMethod === PaymentMethod.Pix && tenantPreference.inStorePixKey && (
                  <InStorePixStep pixKey={tenantPreference.inStorePixKey} amount={isNoProductsMode ? (manualOrderValue ?? 0) : total - couponDiscountAmount} />
                )}
                {selectedPaymentMethod === PaymentMethod.BankingBillet && bankingBilletResult && (
                  <BankingBilletStep billetUrl={bankingBilletResult.billetLink} billetPdf={bankingBilletResult.billetPdf} expiration={bankingBilletResult.expiration} />
                )}
              </>
            )}
            {showSummary && (
              <div className="flex flex-col gap-4 lg:w-[340px] xl:w-[380px] lg:shrink-0">
                {selectedCustomerId && selectedCustomerInfo && (
                  <div className="flex flex-col bg-white border p-4 gap-3 rounded shadow">
                    <div className="flex items-center gap-2">
                      <FaUser className="text-primary-600" size={14} />
                      <h4 className="text-md font-semibold">CLIENTE SELECIONADO</h4>
                    </div>
                    <div className="flex justify-between items-center px-1">
                      <p className="text-xs">{selectedCustomerInfo.customerType === CustomerType.IndividualPerson ? "Nome" : "Razão Social"}:</p>
                      <p className="text-sm min-w-0 break-words text-right">{selectedCustomerInfo.name}</p>
                    </div>
                    <div className="flex justify-between items-center px-1">
                      <p className="text-xs">{selectedCustomerInfo.customerType === CustomerType.IndividualPerson ? "CPF" : "CNPJ"}:</p>
                      <p className="text-sm min-w-0 break-words text-right">{selectedCustomerInfo.document || "-"}</p>
                    </div>
                  </div>
                )}
                {currentStep > 0 && (
                  <OrderSummary
                    getTotalCartItems={isNoProductsMode ? () => manualOrderValue : getTotal}
                    orderCouponDiscount={couponDiscountAmount ?? null}
                    orderShippingPrice={null}
                    orderShippingMethod={null}
                    orderBankingBilletFee={
                      selectedPaymentMethod === PaymentMethod.BankingBillet
                        ? bankingBilletFeeValue
                        : 0
                    }
                    orderBaseBankingBilletFee={tenantPreference?.bankingBilletFee ?? null}
                    orderInterest={creditCardInterest}
                    orderFinalPrice={
                      isNoProductsMode
                        ? (manualOrderValue ?? 0) + (
                            selectedPaymentMethod === PaymentMethod.BankingBillet ? bankingBilletFeeValue
                            : selectedPaymentMethod === PaymentMethod.CreditCard ? creditCardInterest
                            : 0)
                        : total - couponDiscountAmount +
                          (selectedPaymentMethod === PaymentMethod.BankingBillet ? bankingBilletFeeValue : 0) +
                          (selectedPaymentMethod === PaymentMethod.CreditCard ? creditCardInterest : 0)
                    }
                  />
                )}
                {currentStep === 2 && !isNoProductsMode && (
                  <CouponInput appliedCoupon={appliedCoupon ?? undefined} onApply={handleApplyCoupon} onRemove={handleRemoveCoupon} />
                )}
                <OrderNavigation
                  isOrderStep={currentStep === steps.length - 1}
                  hideBackButton={currentStep === 0 || (isOrderCompleteStep && !isPixCompleteStep)}
                  onNext={nextStep}
                  onBack={prevStep}
                  showCancelButton={currentStep === 0}
                  onCancel={() => navigate(basePath + "/balcao")}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminInStoreOrderNewPage;
